import axios, { AxiosRequestConfig } from 'axios';
import { UploadToTempResults } from '@api/v1';
import _ from 'lodash';
import { DEFAULT_TIMEOUT_MS, LONG_RUNNING_TIMEOUT_MS, withRetry } from './resilience';

export const api = {
	// GET is idempotent, so a transient failure is safe to retry; the mutating
	// verbs below are not — see `withRetry` in ~lib/resilience.
	async get<Response = any>(url: string, config?: AxiosRequestConfig): Promise<Response> {
		const requestConfig = mergeConfig(_.merge(config, { params: { _: Date.now() } }));
		const { data } = await withRetry(
			(timeout) => axios.get<Response>(url, { ...requestConfig, timeout }),
			requestConfig.timeout
		);
		return data;
	},
	async post<Body = any, Response = any>(
		url: string,
		body?: Body,
		config?: AxiosRequestConfig
	): Promise<Response> {
		const { data } = await axios.post<Response>(url, body, mergeConfig(config));
		return data;
	},
	async put<Body = any, Response = any>(
		url: string,
		body?: Body,
		config?: AxiosRequestConfig
	): Promise<Response> {
		const { data } = await axios.put<Response>(url, body, mergeConfig(config));
		return data;
	},
	async delete<Response = any>(url: string, config?: AxiosRequestConfig): Promise<Response> {
		const { data } = await axios.delete(url, mergeConfig(config));
		return data;
	},
	async patch<Body = any, Response = any>(
		url: string,
		body?: Body,
		config?: AxiosRequestConfig
	): Promise<Response> {
		const { data } = await axios.patch<Response>(url, body, mergeConfig(config));
		return data;
	},
	async uploadFile(
		file: File,
		config?: AxiosRequestConfig,
		casavi = false
	): Promise<UploadToTempResults> {
		const formData = new FormData();
		formData.append('file', file);
		return axios
			.post<UploadToTempResults>(
				`/api/v1/uploads${casavi ? '/casavi' : ''}`,
				formData,
				mergeConfig(_.merge({ timeout: LONG_RUNNING_TIMEOUT_MS }, config))
			)
			.then(({ data }) => data);
	}
};

function mergeConfig(config: AxiosRequestConfig): AxiosRequestConfig {
	const jwt = window.localStorage.getItem('jwt');
	const preconfig: AxiosRequestConfig = {
		headers: {
			Accept: 'application/json',
			Authorization: jwt ? `Bearer ${jwt}` : undefined
		},
		timeout: DEFAULT_TIMEOUT_MS,
		timeoutErrorMessage: `Timeout Error: timeout ${DEFAULT_TIMEOUT_MS / 1000}s`
	};
	return _.merge(preconfig, config);
}
